import { PublicClientApplication, type Configuration } from '@azure/msal-browser';

/**
 * MSAL configuration for Entra sign-in.
 *
 * Auth code + PKCE, so there is no client secret in this bundle. The client id
 * and tenant id ARE inlined at build time, which is correct — neither is a
 * secret.
 *
 * The tenant differs per environment: the microstrat-projects.com directory in
 * development, 1320cc.com in production. VITE_ENTRA_TENANT_ID carries that
 * directory's Directory (tenant) ID GUID, never the domain — the server
 * compares it against the token's tid claim, which is always the GUID.
 */
export interface MsalEnv {
  clientId?: string;
  tenantId?: string;
  origin: string;
}

export const buildMsalConfig = ({ clientId, tenantId, origin }: MsalEnv): Configuration => ({
  auth: {
    clientId: clientId ?? '',
    authority: `https://login.microsoftonline.com/${tenantId ?? ''}`,
    // Derived from the running origin so one build works on localhost, behind
    // the Caddy host, and on App Service. Every value used here must also be
    // registered as a redirect URI on the app registration.
    redirectUri: origin,
    postLogoutRedirectUri: `${origin}/login`,
    // No `navigateToLoginRequestUrl` / `storeAuthStateInCookie` here: the
    // installed @azure/msal-browser (^5) dropped both from Configuration.
    // `navigateToLoginRequestUrl` is now a per-call option on
    // handleRedirectPromise/RedirectRequest, not a global auth setting, and
    // `storeAuthStateInCookie` was the IE11 cookie fallback, removed with IE11
    // support. Neither has a replacement needed here.
  },
  cache: {
    // Matches where the app JWT lives, so closing the tab ends both.
    cacheLocation: 'sessionStorage',
  },
});

let instance: PublicClientApplication | null = null;

/**
 * Built lazily, and only when something actually signs in.
 *
 * Constructing at module scope would run on import: it needs `window`, and it
 * throws when no client id is configured — so a build with Entra switched off,
 * or a test that imports this module for `buildMsalConfig` alone, would break
 * on the import itself.
 */
export const getMsalInstance = (): PublicClientApplication => {
  if (!instance) {
    instance = new PublicClientApplication(
      buildMsalConfig({
        clientId: import.meta.env.VITE_ENTRA_CLIENT_ID,
        tenantId: import.meta.env.VITE_ENTRA_TENANT_ID,
        origin: window.location.origin,
      })
    );
  }
  return instance;
};

// An ID token is all the server needs. No Graph access is requested.
export const loginRequest = { scopes: ['openid', 'profile', 'email'] };
